<template>
  <div class="erp-finance-container">
    <!-- 头部工具栏 -->
    <div class="erp-header">
      <div class="header-left">
        <h2 class="erp-title">
          <i class="el-icon-s-finance"></i>
          资金账户总汇
        </h2>
        <div class="erp-status">
          <el-tag type="success" size="small">
            共 {{ displayData.length }} 个科目
          </el-tag>
          <el-tag type="warning" size="small" style="margin-left: 8px;">
            合计: {{ formatMoney(totalAmount) }}
          </el-tag>
        </div>
      </div>
      <div class="header-right">
        <div class="erp-actions">
          <el-button
              type="primary"
              size="mini"
              :loading="loading"
              @click="initData"
          >刷新</el-button>
        </div>
      </div>
    </div>

    <div class="erp-main-content">
      <div class="erp-table-panel" style="width: 100%;">
        <div class="panel-header">
          <div class="panel-title">
            <i class="el-icon-s-grid"></i>
            <span>资金账户明细表</span>
          </div>
          <div class="panel-actions">
            <el-tooltip content="仅展示，不支持编辑" placement="top">
              <i class="el-icon-info" style="color: #909399; font-size: 16px;"></i>
            </el-tooltip>
          </div>
        </div>

        <div class="table-container" v-loading="loading">
          <el-table
              :data="displayData"
              border
              size="small"
              style="width: 100%"
              :header-cell-style="{ background: '#f8f9fa', color: '#606266' }"
              class="erp-table"
              row-key="id"
          >
            <el-table-column label="序号" width="60" align="center" fixed>
              <template slot-scope="scope">
                <span class="row-index">{{ scope.$index + 1 }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="name" label="科目" min-width="250">
              <template slot-scope="{ row }">
                <div class="name-cell" :style="{ paddingLeft: (row.level || 0) * 24 + 'px' }">
                  <i :class="getNodeIcon(row)" :style="{ color: getNodeColor(row), marginRight: '8px' }"></i>
                  <span>{{ row.name }}</span>
                </div>
              </template>
            </el-table-column>

            <el-table-column prop="amount" label="金额（元）" min-width="250">
              <template slot-scope="{ row }">
                <div v-if="row.hasChildren" class="amount-sum">
                  <span class="sum-value">{{ formatMoney(row.amount) }}</span>
                </div>
                <div v-else class="amount-readonly">
                  <span class="readonly-value">{{ formatMoney(row.amount) }}</span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllFinanceclass, getFundList } from "@/api/clasificacionContable/clasificacionContable";

export default {
  name: 'FundAccountSummary',
  data() {
    return {
      loading: false,
      tableData: [],
      displayData: []
    }
  },
  computed: {
    totalAmount() {
      // tableData 只有三个顶层节点：现金、银行账号、聚合支付
      return this.tableData.reduce((sum, item) => {
        return sum + (Number(item.amount) || 0)
      }, 0)
    }
  },

  created() {
    this.initData()
  },

  methods: {
    async initData() {
      await this.loadData()
    },

    async loadData() {
      this.loading = true
      try {
        const [classRes, fundRes] = await Promise.all([
          getAllFinanceclass(),
          getFundList()
        ])

        // 1. 建立 id -> { name, parentId } 映射（递归遍历科目树）
        const tree = classRes?.children || classRes?.data?.children || []
        const idMap = this.buildIdMap(tree)

        // 2. 建立 financeId -> amount 映射
        const fundMap = {}
        const fundList = fundRes?.data || []
        fundList.forEach(item => {
          fundMap[String(item.financeId)] = Number(item.amount) || 0
        })

        // 3. 构建表格
        this.buildTableData(idMap, fundMap)
        this.updateDisplayData()
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败：' + (error.message || '网络错误'))
      } finally {
        this.loading = false
      }
    },

    buildIdMap(tree) {
      const map = {}
      const walk = (nodes, parentId = null) => {
        if (!nodes || !nodes.length) return
        nodes.forEach(n => {
          const id = String(n.id)
          map[id] = {
            id,
            name: n.label || n.text || n.name || '',
            parentId: parentId ? String(parentId) : null,
            children: n.children || []
          }
          if (n.children?.length) walk(n.children, id)
        })
      }
      walk(tree)
      return map
    },

    // 判断 nodeId 是否在 ancestorId 的子树中
    isDescendantOf(idMap, nodeId, ancestorId) {
      let cur = String(nodeId)
      let guard = 0
      while (cur && guard < 100) {
        if (cur === String(ancestorId)) return true
        cur = idMap[cur]?.parentId
        guard++
      }
      return false
    },

    buildTableData(idMap, fundMap) {
      this.tableData = []

      const CASH_ID = '101'
      const BANK_ID = '102'

      // 1. 现金
      const cashInfo = idMap[CASH_ID]
      this.tableData.push({
        id: 'cash',
        financeId: CASH_ID,
        name: cashInfo?.name || '现金',
        amount: fundMap[CASH_ID] || 0,
        level: 0,
        parentId: null,
        hasChildren: false
      })

      // 2. 银行账号（含所有 102 的后代）
      const bankChildren = []
      Object.keys(fundMap).forEach(fid => {
        if (fid === BANK_ID) return
        if (!idMap[fid]) return
        if (this.isDescendantOf(idMap, fid, BANK_ID)) {
          bankChildren.push({
            id: `bank_${fid}`,
            financeId: fid,
            name: idMap[fid].name || `银行${fid}`,
            amount: fundMap[fid] || 0,
            level: 1,
            parentId: 'bank_parent',
            hasChildren: false
          })
        }
      })
      const bankParent = {
        id: 'bank_parent',
        financeId: BANK_ID,
        name: idMap[BANK_ID]?.name || '银行账号',
        amount: bankChildren.reduce((s, c) => s + (Number(c.amount) || 0), 0),
        level: 0,
        parentId: null,
        hasChildren: true,
        children: bankChildren
      }
      this.tableData.push(bankParent)

      // 3. 聚合支付（既不是 101 也不是 102 子树中的资金科目）
      const gatherChildren = []
      Object.keys(fundMap).forEach(fid => {
        if (fid === CASH_ID || fid === BANK_ID) return
        if (!idMap[fid]) return
        if (this.isDescendantOf(idMap, fid, BANK_ID)) return
        if (this.isDescendantOf(idMap, fid, CASH_ID)) return
        gatherChildren.push({
          id: `gather_${fid}`,
          financeId: fid,
          name: idMap[fid].name || `聚合支付${fid}`,
          amount: fundMap[fid] || 0,
          level: 1,
          parentId: 'gather_parent',
          hasChildren: false
        })
      })
      const gatherParent = {
        id: 'gather_parent',
        financeId: null,
        name: '聚合支付',
        amount: gatherChildren.reduce((s, c) => s + (Number(c.amount) || 0), 0),
        level: 0,
        parentId: null,
        hasChildren: true,
        children: gatherChildren
      }
      this.tableData.push(gatherParent)
    },

    updateDisplayData() {
      const displayList = []
      const addNode = (node) => {
        displayList.push(node)
        if (node.children && node.children.length) {
          node.children.forEach(child => addNode(child))
        }
      }
      this.tableData.forEach(node => addNode(node))
      this.displayData = displayList
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '0.00'
      const num = parseFloat(value)
      if (isNaN(num)) return '0.00'
      return num.toFixed(2)
    },

    getNodeIcon(data) {
      if (data.hasChildren) return 'el-icon-folder-opened'
      if (data.name === '现金') return 'el-icon-money'
      if (data.parentId === 'bank_parent') return 'el-icon-bank-card'
      if (data.parentId === 'gather_parent') return 'el-icon-connection'
      return 'el-icon-document'
    },

    getNodeColor(data) {
      if (data.hasChildren) return '#409EFF'
      if (data.name === '现金') return '#67C23A'
      if (data.parentId === 'bank_parent') return '#409EFF'
      if (data.parentId === 'gather_parent') return '#9B59B6'
      return '#909399'
    }
  }
}
</script>

<style lang="less" scoped>
.erp-finance-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  padding: 20px;

  .erp-header {
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-left {
      .erp-title {
        margin: 0;
        font-size: 20px;
        color: #303133;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          font-size: 24px;
          color: #409EFF;
        }
      }

      .erp-status {
        margin-top: 8px;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 20px;
    }
  }

  .erp-main-content {
    flex: 1;
    display: flex;
    gap: 20px;
    overflow: hidden;

    .erp-table-panel {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      min-width: 0;

      .panel-header {
        padding: 16px 20px;
        border-bottom: 1px solid #ebeef5;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .panel-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #303133;

          i {
            color: #409EFF;
          }
        }

        .panel-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
      }

      .table-container {
        flex: 1;
        padding: 20px;
        overflow-y: auto;

        .erp-table {
          /deep/ .el-table__header-wrapper {
            th {
              font-weight: 600;
            }
          }

          /deep/ .el-table__body-wrapper {
            tr {
              &:hover {
                td {
                  background-color: #f8f9fa;
                }
              }
            }
          }

          .row-index {
            color: #909399;
            font-weight: 500;
          }

          .name-cell {
            display: flex;
            align-items: center;
          }

          .amount-sum {
            display: flex;
            align-items: center;
            justify-content: flex-start;

            .sum-value {
              font-weight: 600;
              color: #E6A23C;
              font-size: 14px;
            }
          }

          .amount-readonly {
            display: flex;
            align-items: center;
            justify-content: flex-start;

            .readonly-value {
              font-weight: 500;
              color: #909399;
              font-size: 14px;
            }
          }
        }
      }
    }
  }
}

.table-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>